import { useGetApiInstitution, useGetApiInstitutionCurrent } from '@smartsash/api-client';
import type { SelectOption } from '@smartsash/ui';
import { useMemo } from 'react';

import { LOOKUP_PAGE_SIZE } from '@/hooks/useLookupNames';

export interface InstitutionOptions {
  options: SelectOption[];
  /** Preselect this id when the caller works inside exactly one institution. */
  defaultId: string | undefined;
  /** The caller has no choice to make — render the field read-only. */
  isFixed: boolean;
  isLoading: boolean;
  /** No institution could be resolved at all: the field cannot be satisfied. */
  isEmpty: boolean;
}

/**
 * The Institution selector's options, for every form that has to name one (HS-1202, HS-1285).
 *
 * **Why a fallback exists.** `GET /api/Institution` filters by the caller's *institution-level*
 * scope grants — `InstitutionController.cs` narrows the query to
 * `userScope.InstitutionIds.Contains(l.Id)` unless the caller is a system user. An institution
 * admin whose scope sits on a group or a lab matches nothing, so the endpoint answers with an
 * empty page. Each form then mapped `items ?? []` into an empty option list, and the required
 * field could never be satisfied: the group and laboratory forms simply could not be submitted.
 *
 * **Why `/current` is the right fallback.** It resolves the institution from the caller's own
 * claim rather than from the request, carries no scope check, and needs nothing beyond
 * authentication — reading one's own institution is not a management capability. `id` and `name`
 * are all a selector needs, which is exactly what `InstitutionProfileDTO` carries.
 *
 * **Why the field becomes read-only.** An institution admin belongs to exactly one institution.
 * Offering a dropdown with one entry is not a choice, it is a step; `isFixed` lets the form say so.
 *
 * The fallback is disabled while the list is loading and whenever it returned anything, so a
 * Hawkins admin never issues the second request.
 *
 * `enabled: false` sends neither request and resolves to no options — for a screen whose caller
 * may lack what `GET /api/Institution` needs, where the read would only be refused (HS-1548, M9).
 */
export function useInstitutionOptions({
  enabled = true,
}: { enabled?: boolean } = {}): InstitutionOptions {
  const list = useGetApiInstitution(
    { page: 1, pageSize: LOOKUP_PAGE_SIZE },
    { query: { enabled } },
  );
  const items = useMemo(() => list.data?.items ?? [], [list.data]);
  const needsFallback = enabled && !list.isLoading && items.length === 0;

  const own = useGetApiInstitutionCurrent({ query: { enabled: needsFallback } });

  return useMemo(() => {
    if (!enabled) {
      return { options: [], defaultId: undefined, isFixed: false, isLoading: false, isEmpty: true };
    }
    if (items.length > 0) {
      return {
        options: items.map((institution) => ({
          value: institution.id ?? '',
          label: institution.name ?? '',
        })),
        defaultId: undefined,
        isFixed: false,
        isLoading: list.isLoading,
        isEmpty: false,
      };
    }

    const ownInstitution = own.data;
    if (needsFallback && ownInstitution?.id) {
      return {
        options: [{ value: ownInstitution.id, label: ownInstitution.name ?? '' }],
        defaultId: ownInstitution.id,
        isFixed: true,
        isLoading: false,
        isEmpty: false,
      };
    }

    const isLoading = list.isLoading || (needsFallback && own.isLoading);
    return { options: [], defaultId: undefined, isFixed: false, isLoading, isEmpty: !isLoading };
  }, [enabled, items, list.isLoading, needsFallback, own.data, own.isLoading]);
}
